import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PhoneCall, Clock, CheckCircle2, Archive, User, Package } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'closed', label: 'Closed' },
  { value: 'all', label: 'All' },
];

function statusBadge(status) {
  const map = {
    pending: 'bg-[#0B4F3F]/10 text-[#0B4F3F]',
    contacted: 'bg-[#15803D]/10 text-[#15803D]',
    closed: 'bg-[#5B6675]/10 text-[#5B6675]',
  };
  return `text-[10px] font-bold rounded-full px-2 py-0.5 ${map[status] || 'bg-[#EFF2F7] text-[#5B6675]'}`;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

export default function OrgRequests() {
  const { api } = useAuth();
  const [filter, setFilter] = useState('pending');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, contacted: 0, closed: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/orgs/me/callback-requests', {
        params: { status: filter === 'all' ? undefined : filter },
      });
      setItems(data.requests || []);
      setCounts(data.counts || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [api, filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="org-requests">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 text-center">
          <Clock className="w-4 h-4 mx-auto text-[#0B4F3F] mb-1" />
          <p className="text-lg font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{counts.pending || 0}</p>
          <p className="text-[10px] text-[#5B6675]">Pending</p>
        </div>
        <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 text-center">
          <CheckCircle2 className="w-4 h-4 mx-auto text-[#15803D] mb-1" />
          <p className="text-lg font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{counts.contacted || 0}</p>
          <p className="text-[10px] text-[#5B6675]">Contacted</p>
        </div>
        <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 text-center">
          <Archive className="w-4 h-4 mx-auto text-[#5B6675] mb-1" />
          <p className="text-lg font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{counts.closed || 0}</p>
          <p className="text-[10px] text-[#5B6675]">Closed</p>
        </div>
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="w-full">
        <TabsList className="w-full bg-[#EFF2F7] rounded-xl p-1 h-auto">
          {STATUS_TABS.map(t => (
            <TabsTrigger key={t.value} value={t.value} data-testid={`org-requests-tab-${t.value}`} className="flex-1 rounded-lg text-xs data-[state=active]:bg-[#F6F8FB] data-[state=active]:text-[#0B4F3F] data-[state=active]:shadow-sm">
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-4 h-20 animate-pulse" />
      ) : items.length === 0 ? (
        <div className="text-center py-12">
          <PhoneCall className="w-10 h-10 mx-auto text-[#5B6675]/30 mb-3" />
          <p className="text-sm text-[#5B6675]">No requests yet</p>
        </div>
      ) : (
        items.map(r => (
          <div key={r.id} data-testid={`org-request-row-${r.id}`} className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 space-y-2">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#0B1620] flex items-center gap-1 truncate">
                  <Package className="w-3 h-3 text-[#0B4F3F]" /> {r.post_title || 'Listing'}
                </p>
                <p className="text-[11px] text-[#5B6675]">{formatDate(r.created_at)}</p>
              </div>
              <span className={statusBadge(r.status)}>{r.status.toUpperCase()}</span>
            </div>
            <div className="text-xs grid grid-cols-2 gap-2">
              <div className="bg-[#EFF2F7] rounded p-2">
                <p className="text-[10px] uppercase text-[#5B6675] flex items-center gap-1"><User className="w-3 h-3" />Vendor</p>
                <p className="text-[#0B1620] font-medium">{r.user_name}</p>
                <p className="text-[#0B4F3F]">{r.user_phone}</p>
              </div>
              <div className="bg-[#EFF2F7] rounded p-2">
                <p className="text-[10px] uppercase text-[#5B6675] flex items-center gap-1"><PhoneCall className="w-3 h-3" />Farmer</p>
                <p className="text-[#0B1620] font-medium">{r.farmer_name}</p>
              </div>
            </div>
            {r.admin_notes && (
              <p className="text-[11px] text-[#5B6675] bg-[#F6F8FB] rounded p-2 border border-[#E4E8EF]">
                Frootex team note: {r.admin_notes}
              </p>
            )}
          </div>
        ))
      )}
    </div>
  );
}
